import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { apiService, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { fmtDate, StatusPill } from './AdminModeration';
import { TicketDetail } from './AdminTickets';
import { ReportDetail } from './AdminReports';

export const SupportTab = ({ s, reload }) => {
  const [openId, setOpenId] = useState(null);
  const t = s.support.tickets;
  return (
    <div className="space-y-3" data-testid="member-tab-support">
      <p className="text-sm text-gray-600">Tickets submitted with this member's email address. Replies are emailed from noreply@flamelink.site.</p>
      {t.length === 0 && <Card className="p-6 text-center text-gray-500" data-testid="support-empty">No support tickets.</Card>}
      {t.map((tk) => (
        <Card key={tk.id} className="p-4" data-testid={`member-ticket-${tk.id}`}>
          <button type="button" className="w-full text-left flex flex-wrap items-center gap-3" onClick={() => setOpenId(openId === tk.id ? null : tk.id)} data-testid={`member-ticket-toggle-${tk.id}`}>
            <StatusPill status={tk.status} /><span className="font-mono text-xs text-gray-500">#{tk.id.slice(0, 8)}</span><span className="text-xs uppercase text-gray-500">{tk.kind}</span><span className="font-semibold">{tk.subject || tk.category || '(no subject)'}</span><span className="ml-auto text-xs text-gray-400">{fmtDate(tk.created_at)}</span>
          </button>
          {openId === tk.id && <TicketDetail ticket={tk} onUpdated={reload} />}
        </Card>
      ))}
    </div>
  );
};

const ReportList = ({ items, nameKey, label, reload, testId }) => {
  const [openId, setOpenId] = useState(null);
  return (
    <div className="space-y-2" data-testid={testId}>
      <h4 className="font-semibold text-sm">{label} ({items.length})</h4>
      {items.length === 0 && <p className="text-sm text-gray-500">None.</p>}
      {items.map((r) => (
        <Card key={r.id} className="p-3">
          <button type="button" className="w-full text-left flex flex-wrap items-center gap-3 text-sm" onClick={() => setOpenId(openId === r.id ? null : r.id)} data-testid={`member-report-toggle-${r.id}`}>
            <StatusPill status={r.status} /><span className="font-mono text-xs text-gray-500">#{r.id.slice(0, 8)}</span><span className="font-medium">{r.category}</span><span className="text-gray-600">{nameKey === 'reporter_name' ? 'by' : 'against'} {r[nameKey] || 'unknown member'}</span><span className="ml-auto text-xs text-gray-400">{fmtDate(r.created_at)}</span>
          </button>
          {openId === r.id && <ReportDetail report={r} onUpdated={reload} />}
        </Card>
      ))}
    </div>
  );
};

export const ModerationTab = ({ s, reload }) => (
  <div className="space-y-6" data-testid="member-tab-moderation">
    <div className="grid sm:grid-cols-2 gap-3 text-sm">
      <Card className="p-3"><span className="text-gray-500">Blocked by others:</span> <b data-testid="mod-blocked-by">{s.activity.blocked_by}</b></Card>
      <Card className="p-3"><span className="text-gray-500">Has blocked:</span> <b data-testid="mod-blocks-made">{s.activity.blocks_made}</b></Card>
    </div>
    <ReportList items={s.moderation.reports_against} nameKey="reporter_name" label="Reports against this member" reload={reload} testId="mod-reports-against" />
    <ReportList items={s.moderation.reports_by} nameKey="reported_name" label="Reports filed by this member" reload={reload} testId="mod-reports-by" />
    <p className="text-xs text-gray-500">Private conversations between members are not shown here.</p>
  </div>
);

export const NotesTab = ({ s, reload }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const add = async () => {
    setBusy(true);
    try { await apiService.admin.addMemberNote(s.member.id, note.trim()); setNote(''); toast({ title: 'Note added' }); reload(); }
    catch (e) { toast({ title: 'Could not add note', description: formatApiError(e), variant: 'destructive' }); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="member-tab-notes">
      <Card className="p-4 space-y-2">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} placeholder="Private administrative note (never visible to the member)" data-testid="member-note-input" />
        <Button size="sm" onClick={add} disabled={busy || !note.trim()} data-testid="member-note-add-btn">{busy ? 'Saving…' : 'Add private note'}</Button>
      </Card>
      {s.legacy_admin_note && <Card className="p-3 text-sm bg-gray-50"><span className="text-gray-500">Legacy note (editable via Edit profile):</span> {s.legacy_admin_note}</Card>}
      {s.notes.length === 0 && <p className="text-sm text-gray-500" data-testid="notes-empty">No notes yet.</p>}
      {s.notes.map((n) => <Card key={n.id} className="p-3 text-sm" data-testid={`member-note-${n.id}`}><p className="whitespace-pre-wrap">{n.note}</p><p className="text-xs text-gray-400 mt-1">{n.admin_username} · {fmtDate(n.created_at)}</p></Card>)}
    </div>
  );
};

const describe = (a) => {
  const d = a.action_details || {};
  if (d.changes) return Object.entries(d.changes).map(([k, v]) => `${k}: "${String(v.before ?? '—').slice(0, 40)}" → "${String(v.after ?? '—').slice(0, 40)}"`).join('; ');
  return Object.entries(d).filter(([k]) => k !== 'note_id').map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('; ');
};

export const ActivityTab = ({ s }) => {
  const a = s.activity;
  return (
    <div className="space-y-4" data-testid="member-tab-activity">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        {[['Swipes given', a.swipes_given], ['Likes given', a.likes_given], ['Likes received', a.likes_received], ['Matches', a.matches], ['Messages sent', a.messages_sent], ['Last message', a.last_message_at ? fmtDate(a.last_message_at) : '—'], ['Last login', fmtDate(a.last_login_at)], ['Member stories', s.stories.length]].map(([k, v]) => <Card key={k} className="p-3"><p className="text-xs uppercase text-gray-500">{k}</p><p className="font-semibold">{v}</p></Card>)}
      </div>
      <Card className="p-4">
        <h4 className="font-semibold mb-2">Administrative actions on this member ({s.audit.length})</h4>
        {s.audit.length === 0 && <p className="text-sm text-gray-500" data-testid="audit-empty">No administrative actions recorded.</p>}
        <ul className="divide-y text-sm">{s.audit.map((x) => <li key={x.id} className="py-2" data-testid={`audit-${x.id}`}><span className="font-medium">{x.action_type}</span> <span className="text-gray-500">by {x.admin_username} · {fmtDate(x.timestamp)}</span><p className="text-gray-600 text-xs break-all">{describe(x)}</p></li>)}</ul>
      </Card>
    </div>
  );
};
